// Outstanding Jobs dashboard panel (Session AAJ, Item 1): scheduled + in_progress
// maintenance work the engineer has not completed, most overdue/imminent first. Self
// fetches (its own loading/error) so it never blocks the tiles. Job type colour + label
// come from the shared src/lib/jobType.ts helper (never inlined). Links: Site and Lift to
// their detail pages, the row arrow to the office job view (no /office/jobs/:id route in V2,
// so jobs are opened via /office/jobsheets?job=:id).
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import type { OutstandingJobs } from "../../lib/api";
import JobTypeBadge from "../../components/JobTypeBadge";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { Loader } from "../../components/Loader";
import { StatusPill } from "../../components/StatusPill";
import { formatDate } from "../../lib/format";

// "Not set" for a job with no date (formatDate alone would print "-").
function scheduledText(iso: string | null): string {
  const text = formatDate(iso);
  return text === "-" ? "Not set" : text;
}

// True when the ISO date falls on today's UTC day (used for the amber "due today" colour;
// the server already flags past days as overdue).
function isDueTodayUtc(iso: string | null): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  return (
    d.getUTCFullYear() === now.getUTCFullYear() &&
    d.getUTCMonth() === now.getUTCMonth() &&
    d.getUTCDate() === now.getUTCDate()
  );
}

function dueClass(iso: string | null, isOverdue: boolean): string {
  if (isOverdue) return "text-ele-danger";
  if (isDueTodayUtc(iso)) return "text-amber-400";
  return "text-ele-body";
}

export function OutstandingJobsPanel() {
  const navigate = useNavigate();
  const [data, setData] = useState<OutstandingJobs | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // Card list below md, table otherwise -- decided once at mount (Session AGZ's mechanism, the
  // same one InvoicesPage and the other converted office lists use).
  const [mobileCards] = useState(initialMobileLayout);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(false);
    api.dashboard
      .outstandingJobs()
      .then((d) => {
        if (live) setData(d);
      })
      .catch(() => {
        if (live) setError(true);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [reloadKey]);

  const jobs = data?.jobs ?? [];
  // Shared between the card and table branches so the two never drift apart.
  const viewAllLink =
    jobs.length === 10 ? (
      <div className="mt-3 text-right">
        {/* Outstanding jobs are scheduled / in progress and have no service record yet, so
            Service Records cannot list them. The Scheduling calendar is where these visits
            live and can be actioned, so "View all" goes there (not to the empty jobsheets
            list). */}
        <Link to="/office/scheduling" className="ele-view-all">
          View all outstanding jobs &#8594;
        </Link>
      </div>
    ) : null;

  return (
    <section className="mb-6 rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
      <div className="mb-3 flex items-center gap-3">
        <h2 className="ele-section-title">Outstanding Jobs</h2>
        {!loading && !error ? (
          <span className="rounded-full bg-ele-inset px-2 py-0.5 font-menu text-xs text-ele-strong">{jobs.length}</span>
        ) : null}
        {!loading && !error && data && data.overdueCount > 0 ? (
          <span className="font-menu text-xs uppercase tracking-wide text-ele-danger">{data.overdueCount} overdue</span>
        ) : null}
      </div>

      {loading ? (
        <Loader rows={3} />
      ) : error ? (
        <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center">
          <p role="alert" className="text-ele-strong">
            Could not load jobs.
          </p>
          <Button variant="secondary" size="sm" className="mt-3" onClick={() => setReloadKey((k) => k + 1)}>
            Retry
          </Button>
        </div>
      ) : jobs.length === 0 ? (
        <EmptyState message="All clear. No outstanding jobs." icon={<span aria-hidden className="text-ele-green">&#10003;</span>} />
      ) : mobileCards ? (
        <>
          {/* Session AHB pattern: Site is the primary scan field (what the whole card taps
              through to -- the job); Type is the second identifying field; Due is a date (meta,
              still colour-coded by urgency); Status goes in the right rail. Lift, folded away on
              the desktop table below md too, is still useful, so it gets its own details block
              rather than being dropped. */}
          <MobileCardList label="Outstanding jobs">
            {jobs.map((j) => {
              return (
                <MobileListCard
                  key={j.id}
                  to={`/office/jobsheets?job=${encodeURIComponent(j.id)}`}
                  primary={j.site ? j.site.name : "Not linked"}
                  secondary={
                    <span className="flex flex-wrap items-center gap-1">
                      <JobTypeBadge type={j.type} size="sm" />
                      {j.isNonContract && <StatusPill word="Non-contract" tone="amber" />}
                    </span>
                  }
                  meta={<span className={dueClass(j.scheduledFor, j.isOverdue)}>{scheduledText(j.scheduledFor)}</span>}
                  right={
                    <StatusPill domain="job" status={j.status} />
                  }
                  details={
                    <p className="text-sm">
                      <span className="text-ele-body">Lift: </span>
                      {j.lift ? (
                        <Link
                          to={`/office/lifts/${encodeURIComponent(j.lift.id)}`}
                          className="ele-ref underline-offset-2 hover:underline"
                        >
                          {j.lift.liftRef ?? "Unallocated"}
                        </Link>
                      ) : (
                        <span className="text-ele-body">Not linked</span>
                      )}
                    </p>
                  }
                />
              );
            })}
          </MobileCardList>
          {viewAllLink}
        </>
      ) : (
        <>
          <div data-safetynet className="overflow-x-auto">
            {/* Session ACW Loop 6: Site / Type / Due are the priority columns below md (where the
                job is, what kind, and how urgent -- the whole point of this panel); Lift and
                Status are secondary detail folded away on a phone. The desktop-only "Open job"
                arrow is unchanged pixel-for-pixel (now explicitly max-md:hidden); a new mobile-only
                chevron column takes its place below md, and the row itself becomes a guarded tap
                target to the same job (exactly the ContractsPage pattern) so the small arrow icon
                is never the only way to open a job on a phone. */}
            <table className="ele-list-table w-full text-left text-sm">
              <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
                <tr>
                  <th className="px-3 py-2">Site</th>
                  <th className="max-md:hidden px-3 py-2">Lift</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Due</th>
                  <th className="max-md:hidden px-3 py-2">Status</th>
                  <th className="cell-actions max-md:hidden px-3 py-2" aria-label="Open job" />
                  <th className="w-8 cell-actions md:hidden" aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => {
                  return (
                    <tr
                      key={j.id}
                      className="group cursor-pointer border-t border-ele-purple-accent transition hover:bg-ele-row-hover"
                      onClick={(e) => {
                        // A tap anywhere in the row opens the job, except on an interactive
                        // control (the Site/Lift links, the desktop "Open job" arrow) which keep
                        // their own destinations.
                        if ((e.target as HTMLElement).closest("a,button,input,select,textarea,label")) return;
                        navigate(`/office/jobsheets?job=${encodeURIComponent(j.id)}`);
                      }}
                    >
                      <td className="px-3 py-2 align-top">
                        {j.site ? (
                          <Link to={`/office/sites/${encodeURIComponent(j.site.id)}`} className="text-ele-green underline-offset-2 hover:underline">
                            {j.site.name}
                          </Link>
                        ) : (
                          <span className="text-ele-body">Not linked</span>
                        )}
                      </td>
                      <td className="max-md:hidden px-3 py-2 align-top">
                        {j.lift ? (
                          // Reference data is blue (Session ACG colour logic): the ELE number
                          // renders in the monospace reference treatment.
                          <Link to={`/office/lifts/${encodeURIComponent(j.lift.id)}`} className="ele-ref underline-offset-2 hover:underline">
                            {j.lift.liftRef ?? "Unallocated"}
                          </Link>
                        ) : (
                          <span className="text-ele-body">Not linked</span>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <span className="flex flex-wrap items-center gap-1">
                          <JobTypeBadge type={j.type} size="sm" />
                          {/* Session AFY: a walk-in / lapsed-contract call-out is billed on the
                              published non-contract card. The canonical amber pill (same as the
                              Call-outs list badge) flags it here at a glance. Visibility only: it
                              changes nothing about the row or the billing. Sits in the always-visible
                              Type column so it is seen on a phone too (Status folds away below md). */}
                          {j.isNonContract && <StatusPill word="Non-contract" tone="amber" />}
                        </span>
                      </td>
                      <td className={`px-3 py-2 align-top ${dueClass(j.scheduledFor, j.isOverdue)}`}>{scheduledText(j.scheduledFor)}</td>
                      <td className="max-md:hidden px-3 py-2 align-top">
                        {/* Status via the locked six-variant pill system: in progress = blue,
                            scheduled = quiet grey. */}
                        <StatusPill domain="job" status={j.status} />
                      </td>
                      <td className="cell-actions max-md:hidden px-3 py-2 align-top text-right">
                        {/* Row action arrow: purple = navigation intent (Session ACG). */}
                        <Link to={`/office/jobsheets?job=${encodeURIComponent(j.id)}`} className="text-ele-purple transition hover:text-ele-purple-ink" aria-label="Open job">
                          &#8594;
                        </Link>
                      </td>
                      <td className="cell-actions md:hidden pr-1 text-right">
                        <svg aria-hidden viewBox="0 0 16 16" className="ml-auto h-4 w-4 fill-current text-ele-purple transition-colors group-hover:text-ele-green group-focus-within:text-ele-green">
                          <path d="M6 4l4 4-4 4z" />
                        </svg>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {viewAllLink}
        </>
      )}
    </section>
  );
}
